import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useIdleTimeout - Auto-logout after inactivity
 * 
 * Tracks user activity (mouse, keyboard, scroll, touch) and triggers
 * a warning before auto-logout. Resets on any interaction.
 * 
 * @param {Object} options
 * @param {number} options.timeoutMinutes - Minutes of inactivity before logout (default: 10)
 * @param {number} options.warningMinutes - Minutes before logout to show warning (default: 1)
 * @param {Function} options.onLogout - Callback to execute on auto-logout
 * @param {boolean} options.enabled - Whether the timer is active (default: true)
 *
 * suspend()/resume() let long-running work (a big PDF export, a bulk import)
 * hold the countdown while the user sits watching a progress label and
 * produces no input events. Calls nest: the timer only restarts once every
 * suspend has been matched by a resume, and it restarts with a full window
 * rather than resuming a partly elapsed one.
 *
 * @returns {{ showWarning: boolean, remainingSeconds: number, dismissWarning: Function, suspend: Function, resume: Function }}
 */
const useIdleTimeout = ({
  timeoutMinutes = 10,
  warningMinutes = 1,
  onLogout,
  enabled = true
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Use refs so the event handlers always see current values
  // without needing to re-attach listeners on every render
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const onLogoutRef = useRef(onLogout);
  const enabledRef = useRef(enabled);
  // >0 while long-running work is holding the countdown open.
  const suspendCountRef = useRef(0);

  // Keep refs in sync
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;
  const warningAtMs = timeoutMs - warningMs; // When to show the warning

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
    countdownRef.current = null;
  }, []);

  // Execute logout
  const doLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(0);
    if (onLogoutRef.current) {
      onLogoutRef.current();
    }
  }, [clearAllTimers]);

  // Start the countdown display (ticks every second)
  const startCountdown = useCallback(() => {
    let seconds = Math.floor(warningMs / 1000);
    setRemainingSeconds(seconds);
    setShowWarning(true);

    countdownRef.current = setInterval(() => {
      seconds -= 1;
      setRemainingSeconds(seconds);
      if (seconds <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
  }, [warningMs]);

  // Reset the idle timer (called on every user interaction)
  const resetTimer = useCallback(() => {
    if (!enabledRef.current) return;
    // Suspended: stay stopped. resume() restarts the clock.
    if (suspendCountRef.current > 0) return;

    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(0);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      startCountdown();
    }, warningAtMs);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      doLogout();
    }, timeoutMs);
  }, [clearAllTimers, startCountdown, doLogout, warningAtMs, timeoutMs]);

  // Dismiss warning and reset (user clicked "I'm still here")
  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Hold the countdown while long-running work runs. Always pair with resume()
  // in a finally block so a thrown error cannot disable the timeout for good.
  const suspend = useCallback(() => {
    suspendCountRef.current += 1;
    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(0);
  }, [clearAllTimers]);

  const resume = useCallback(() => {
    if (suspendCountRef.current === 0) return;
    suspendCountRef.current -= 1;
    if (suspendCountRef.current === 0) resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle the reset so we're not firing it on every pixel of mouse movement
    let lastReset = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      // Only reset if at least 30 seconds since last reset (unless warning is showing)
      if (now - lastReset > 30000 || showWarning) {
        lastReset = now;
        resetTimer();
      }
    };

    // Attach listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      clearAllTimers();
    };
  }, [enabled]); // Only re-attach when enabled changes

  return {
    showWarning,
    remainingSeconds,
    dismissWarning,
    suspend,
    resume
  };
};

export default useIdleTimeout;
